import type { C1_EloItem_Data, C1_EloItem_Definition } from "../types/C1_EloTypes";
import {
  getEloItemCatalog,
  resolveEloItemDefinition,
  resolveEloItemProfileDefinition,
} from "./C1_EloItemCatalog";
import type { RoomType as C1_RoomType } from "../types/C1_ModelTypes";
import { RoomType, isSpecialExteriorRoomType } from "../types/C1_RoomTypes";
import { C1_EloItem_Type } from "../types/C1_EloTypes";
import { isLivingRoomType } from "./C1_roomPaletteChapters";
import { ChannelType } from "../../../src/models/ChannelType";

const catalog = getEloItemCatalog();
const C1_eloItemsRegistry = new Map<string, C1_EloItem_Definition>([
  ...Object.keys(catalog.items).flatMap((itemId) => {
    const definition = resolveEloItemDefinition(itemId);
    return definition ? [[itemId, definition] as const] : [];
  }),
  ...Object.keys(catalog.profiles).map(
    (profileId) => [profileId, resolveEloItemProfileDefinition(profileId)] as const,
  ),
]);

export default C1_eloItemsRegistry;

export function getEloItemProfile(profileId: string): C1_EloItem_Definition[] {
  const profile = C1_eloItemsRegistry.get(profileId);
  return profile?.type === "group" ? profile.items ?? [] : [];
}

/** Resolves profiles whose concrete item definition depends on the mounted instance. */
export function getEloItemProfileForInstance(
  profileId: string,
  instance: C1_EloItem_Data | undefined,
): C1_EloItem_Definition[] {
  if (profileId !== "electricalLight" && profileId !== "outdoorLight") return getEloItemProfile(profileId);
  const mountedDefinitions = instance?.items.flatMap((item) => {
    const definition = C1_eloItemsRegistry.get(item.definitionId);
    return definition ? [definition] : [];
  });
  return mountedDefinitions?.length ? mountedDefinitions : getEloItemProfile(profileId);
}

export function getRoomEloItemProfile(roomType: C1_RoomType): C1_EloItem_Definition[] {
  const profileId = catalog.roomTypeProfiles[roomType] ?? "room-normal";
  const definitions = getEloItemProfile(profileId);
  if (isSpecialExteriorRoomType(roomType)) {
    return definitions.filter(definition => definition.type !== C1_EloItem_Type.IntermediateDistribution);
  }
  if (roomType === RoomType.Other || !isLivingRoomType(roomType)) return definitions;
  return definitions.map(definition => definition.id === "room.ceilingLight" ? {
    ...definition,
    defaultDetails: { ...definition.defaultDetails, channelType: ChannelType.Dimmer },
    details: { ...definition.details, channelType: {
      ...definition.details?.channelType,
      options: definition.details?.channelType?.options ?? [ChannelType.Switch, ChannelType.Dimmer],
      default: { S1: ChannelType.Dimmer, S2: ChannelType.Dimmer, S3: ChannelType.Dimmer, S4: ChannelType.Dimmer, S5: ChannelType.Dimmer },
    } },
  } : definition);
}
